import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DisplayName } from "@/lib/name";
import {
  Home, Ticket, Users, ClipboardCheck, Plane, CalendarDays, BarChart3,
  Bell, LogOut, ChevronDown, Search as SearchIcon, Eye, EyeOff, Menu,
} from "lucide-react";
import ClientsTab from "@/components/ClientsTab";
import EmissionsTab from "@/components/EmissionsTab";
import ExpensesTab from "@/components/ExpensesTab";
import DashboardOverview from "@/components/DashboardOverview";
import CheckinsDialog from "@/components/CheckinsDialog";
import HomeOverview from "@/components/HomeOverview";
import AgendaTab from "@/components/AgendaTab";
import FinanceTab from "@/components/FinanceTab";
import { usePrivacy } from "@/contexts/PrivacyContext";

const NAV = [
  { key: "home", label: "Início", icon: Home },
  { key: "emissions", label: "Emissões", icon: Ticket },
  { key: "clients", label: "Clientes", icon: Users },
  { key: "checkin", label: "Check-in", icon: ClipboardCheck },
  { key: "agenda", label: "Agenda", icon: CalendarDays },
  { key: "finance", label: "Finanças", icon: BarChart3 },
];

function Sidebar({ current, onChange, mobileOpen, onMobileClose }) {
  const containerCls = `
    ${mobileOpen ? "fixed inset-y-0 left-0 z-50 w-64 flex" : "hidden md:flex md:w-64 md:shrink-0"}
    md:relative flex-col
    bg-slate-950/85 backdrop-blur-lg border-r border-white/10
  `;
  return (
    <>
      {mobileOpen && (
        <button
          type="button"
          aria-label="Fechar menu"
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={onMobileClose}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside className={containerCls} data-testid="sidebar">
        {/* Logo */}
        <div className="h-20 flex items-center px-6 border-b border-white/10">
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_airticket-manager/artifacts/0r3wo3xi_logotipo%20letfly.png"
            alt="Letfly"
            className="h-10 w-auto object-contain"
            data-testid="sidebar-logo"
          />
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {NAV.map((item) => {
            const active = current === item.key;
            const Ico = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => { onChange(item.key); onMobileClose?.(); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                  active
                    ? "bg-white/10 text-white shadow-inner"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`}
                data-testid={`nav-${item.key}`}
              >
                <Ico className={`w-4 h-4 ${active ? "text-orange-400" : "text-slate-400"}`} strokeWidth={1.8} />
                {item.label}
                {item.comingSoon && (
                  <span className="ml-auto text-[10px] uppercase tracking-wide text-slate-500 font-semibold">em breve</span>
                )}
              </button>
            );
          })}
        </nav>
      </aside>
    </>
  );
}

function GlobalSearch({ onNavigate }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ clients: [], emissions: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!q || q.trim().length < 2) {
      setResults({ clients: [], emissions: [] });
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const fmt = (iso) => iso
    ? new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" })
    : "—";

  const handlePick = (kind, item) => {
    setQ("");
    setOpen(false);
    setResults({ clients: [], emissions: [] });
    onNavigate?.(kind, item);
  };

  return (
    <div className="relative w-full">
      <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Buscar cliente, CPF, email..."
        className="pl-9 bg-white/10 border-white/10 text-white placeholder:text-slate-400 h-11 rounded-full focus-visible:ring-orange-400/40"
        data-testid="global-search"
      />
      {open && q.trim().length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-slate-900/95 backdrop-blur-lg border border-white/10 rounded-xl shadow-2xl max-h-[420px] overflow-y-auto z-50 text-slate-100"
          data-testid="global-search-results">
          {loading && <div className="p-3 text-xs text-slate-400">Buscando...</div>}
          {!loading && results.clients.length === 0 && results.emissions.length === 0 && (
            <div className="p-3 text-xs text-slate-400">Nada encontrado</div>
          )}
          {results.clients.length > 0 && (
            <div>
              <div className="px-3 pt-2 pb-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Clientes</div>
              {results.clients.map((c) => (
                <button
                  key={c.client_id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handlePick("client", c)}
                  className="w-full text-left px-3 py-2 hover:bg-white/10 border-t border-white/10 first:border-t-0 cursor-pointer"
                  data-testid={`search-result-client-${c.client_id}`}
                >
                  <div className="text-sm font-medium text-white"><DisplayName name={c.full_name} /></div>
                  <div className="text-xs text-slate-400 flex gap-3">
                    {c.cpf && <span>CPF {c.cpf}</span>}
                    {c.phone && <span>{c.phone}</span>}
                    {c.email && <span className="truncate">{c.email}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
          {results.emissions.length > 0 && (
            <div>
              <div className="px-3 pt-3 pb-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Voos</div>
              {results.emissions.map((e) => (
                <button
                  key={e.emission_id}
                  type="button"
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => handlePick("emission", e)}
                  className="w-full text-left px-3 py-2 hover:bg-white/10 border-t border-white/10 cursor-pointer"
                  data-testid={`search-result-emission-${e.emission_id}`}
                >
                  <div className="text-sm text-white flex items-center gap-1.5 flex-wrap">
                    <b className="text-orange-400">{e.airline}</b>
                    {e.pnr && <span className="text-xs font-mono bg-white/10 px-1 rounded">{e.pnr}</span>}
                    <span>{e.origin} → {e.destination}</span>
                    <span className="text-slate-500">·</span>
                    <span className="text-xs text-slate-400">{fmt(e.departure_at)}</span>
                    {e.passenger_count > 1 && (
                      <span className="text-[10px] font-bold bg-orange-500/25 text-orange-200 px-1.5 py-0.5 rounded">
                        {e.passenger_count} pax
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5"><DisplayName name={e.primary_name} /></div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TopBar({ user, onLogout, onOpenSidebar, onSearchNavigate }) {
  const { blurred, toggle } = usePrivacy();
  return (
    <header className="bg-slate-950/70 backdrop-blur-lg border-b border-white/10 sticky top-0 z-30" data-testid="topbar">
      <div className="min-h-[5rem] px-4 sm:px-6 flex flex-wrap items-center gap-3 sm:gap-6 py-3">
        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={onOpenSidebar}
          className="md:hidden w-10 h-10 rounded-full grid place-items-center hover:bg-white/10 text-slate-200 shrink-0"
          aria-label="Abrir menu"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Search */}
        <div className="flex-1 order-3 md:order-2 min-w-0 basis-full md:basis-auto md:max-w-lg">
          <GlobalSearch onNavigate={onSearchNavigate} />
        </div>

        {/* Notifications + user */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0 order-2 md:order-3 ml-auto">
          <button
            type="button"
            onClick={toggle}
            className="w-10 h-10 rounded-full grid place-items-center hover:bg-white/10"
            title={blurred ? "Mostrar sobrenomes" : "Ocultar sobrenomes"}
            data-testid="toggle-blur-btn"
          >
            {blurred ? (
              <EyeOff className="w-5 h-5 text-slate-300" strokeWidth={1.8} />
            ) : (
              <Eye className="w-5 h-5 text-orange-400" strokeWidth={1.8} />
            )}
          </button>
          <button
            type="button"
            className="relative w-10 h-10 rounded-full grid place-items-center hover:bg-white/10"
            data-testid="notifications-btn"
          >
            <Bell className="w-5 h-5 text-slate-300" strokeWidth={1.8} />
            <span className="absolute top-2 right-2 w-2 h-2 bg-orange-400 rounded-full" />
          </button>
          <div className="flex items-center gap-2 pl-3 border-l border-white/10">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <span className="w-9 h-9 rounded-full bg-white/10 text-orange-300 grid place-items-center text-sm font-bold">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </span>
            )}
            <div className="hidden sm:block text-right">
              <div className="text-sm font-semibold text-white leading-tight" data-testid="header-user-name">
                {user?.name}
              </div>
              <div className="text-xs text-slate-400 leading-tight">{user?.role === "admin" ? "Administrador" : "Agente"}</div>
            </div>
            <button type="button" onClick={onLogout}
              className="ml-1 p-1 rounded hover:bg-white/10 text-slate-400"
              title="Sair" data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </button>
            <ChevronDown className="w-4 h-4 text-slate-500 hidden md:block" />
          </div>
        </div>
      </div>
    </header>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [current, setCurrent] = useState("home");
  const [checkinsOpen, setCheckinsOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const [autoOpenSignal, setAutoOpenSignal] = useState(0);
  const [autoOpenTarget, setAutoOpenTarget] = useState(null);
  const [emissionsInitialSearch, setEmissionsInitialSearch] = useState("");
  const [clientsInitialSearch, setClientsInitialSearch] = useState("");

  const onAnyChange = () => setRefreshKey((k) => k + 1);

  const handleQuickAction = (action) => {
    if (action === "new-client") {
      setCurrent("clients");
      setAutoOpenTarget("clients");
      setAutoOpenSignal((n) => n + 1);
    }
    if (action === "new-emission") {
      setCurrent("emissions");
      setAutoOpenTarget("emissions");
      setAutoOpenSignal((n) => n + 1);
    }
  };

  const handleSearchNavigate = (kind, item) => {
    if (kind === "emission") {
      const q = item.pnr || item.primary_name || "";
      setEmissionsInitialSearch(q);
      setCurrent("emissions");
    } else if (kind === "client") {
      const q = item.full_name || item.cpf || "";
      setClientsInitialSearch(q);
      setCurrent("clients");
    }
  };

  const renderMain = () => {
    switch (current) {
      case "home":
        return (
          <HomeOverview
            key={refreshKey}
            onNavigate={setCurrent}
            onOpenCheckins={() => setCheckinsOpen(true)}
            onQuickAction={handleQuickAction}
          />
        );
      case "emissions":
        return (
          <EmissionsTab
            onChange={onAnyChange}
            autoOpenSignal={autoOpenTarget === "emissions" ? autoOpenSignal : 0}
            initialSearch={emissionsInitialSearch}
          />
        );
      case "clients":
        return (
          <ClientsTab
            onChange={onAnyChange}
            autoOpenSignal={autoOpenTarget === "clients" ? autoOpenSignal : 0}
            initialSearch={clientsInitialSearch}
          />
        );
      case "checkin":
        return (
          <div className="soft-card p-8" data-testid="checkin-panel">
            <h2 className="text-xl font-bold text-slate-900 mb-2">Check-ins</h2>
            <p className="text-slate-500 mb-6 text-sm">Emissões com check-in aberto (até 48h antes do voo).</p>
            <Button
              onClick={() => setCheckinsOpen(true)}
              className="brand-grad text-white"
              data-testid="open-checkins-btn"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" /> Abrir lista de check-ins
            </Button>
          </div>
        );
      case "agenda":
        return <AgendaTab />;
      case "finance":
        return <FinanceTab refreshKey={refreshKey} onChange={onAnyChange} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="dashboard-page">
      <Sidebar
        current={current}
        onChange={setCurrent}
        mobileOpen={mobileSidebar}
        onMobileClose={() => setMobileSidebar(false)}
      />

      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar
          user={user}
          onLogout={logout}
          onOpenSidebar={() => setMobileSidebar(true)}
          onSearchNavigate={handleSearchNavigate}
        />
        <main className="flex-1 p-4 sm:p-6 fade-up overflow-x-hidden">
          {renderMain()}
        </main>
      </div>

      <CheckinsDialog open={checkinsOpen} onOpenChange={setCheckinsOpen} />
    </div>
  );
}
